import { Maximize, Minimize2 } from '@styled-icons/feather';
import { ArrowAutofitHeight } from '@styled-icons/fluentui-system-regular';
import { DreamBlock } from '@typedream/core';
import { getNode, setNodes } from '@udecode/plate-core';
import classNames from 'classnames';
import { memo } from 'react';
import shallow from 'zustand/shallow';
import Tooltip from '../../../components/Tooltip';
import { DEFAULT_MAX_WIDTH } from '../../../constants';
import { hasProperty } from '../../../editorTools/queries';
import { useTPlateEditorRef } from '../../../plate';
import { getSelectedElementEntry } from '../../../plugins/elementSelection';
import { useDreamEditorStore } from '../../../plugins/store';
import { getElementDomRect } from '../../../utils/domNode';
import styles from '../SideToolbar.module.css';

function Sizing() {
  const editor = useTPlateEditorRef();

  const [
    shouldShowWidthControl,
    shouldShowHeightControl,
    isAutoHeight,
  ] = useDreamEditorStore(() => {
    const entry = getSelectedElementEntry(editor);
    if (!entry) return [false, false, false];

    return [
      hasProperty(editor, entry[0], 'width'),
      hasProperty(editor, entry[0], 'height'),
      !(entry[0] as Extract<DreamBlock, { height?: any }>).height,
    ];
  }, shallow);

  const getSetNodePath = () => {
    const selectedElementEntry = getSelectedElementEntry(editor);

    if (selectedElementEntry) {
      const [element, path] = selectedElementEntry;
      let at = path;
      if (element.type === 'li') {
        at = path.slice(0, -1);
      }
      return at;
    }

    return undefined;
  };

  const setToMaxWidth = () => {
    const at = getSetNodePath();
    if (at) {
      setNodes(editor, { width_max: '100%' }, { at });
    }
  };

  const resetResize = () => {
    const at = getSetNodePath();
    if (at) {
      setNodes(
        editor,
        { width_max: undefined, width: DEFAULT_MAX_WIDTH },
        { at },
      );
    }
  };

  const toggleAutoHeight = () => {
    const at = getSetNodePath();
    if (!at) return;

    const element = getNode<Extract<DreamBlock, { height?: any }>>(editor, at);
    if (!element) return;

    const nextHeight = element.height
      ? undefined
      : getElementDomRect(editor, element)?.height;

    setNodes(editor, { height: nextHeight }, { at });
  };

  return (
    <div className={styles.section}>
      <label htmlFor="#" className={styles.label}>
        Sizing
      </label>
      <div className={styles.menuGroups}>
        {shouldShowWidthControl && (
          <div className={styles.menuGroup}>
            <button
              className={styles.menuItem}
              type="button"
              onClick={setToMaxWidth}
            >
              <Tooltip center="Max Width" className="p-1">
                <Maximize size={16} />
              </Tooltip>
            </button>

            <button
              className={styles.menuItem}
              type="button"
              onClick={resetResize}
            >
              <Tooltip center="Reset Width" className="p-1">
                <Minimize2 size={16} />
              </Tooltip>
            </button>
          </div>
        )}

        {shouldShowHeightControl && (
          <div className={styles.menuGroup}>
            <button
              className={classNames(styles.menuItem, {
                [styles.active]: isAutoHeight,
              })}
              type="button"
              onClick={toggleAutoHeight}
            >
              <Tooltip center="Auto Height" className="p-1">
                <ArrowAutofitHeight size={16} />
              </Tooltip>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(Sizing);
